//src/web/Advertisement/Admin/Billing.tsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdvertiserOnboardingLayout from "./AdvertiserOnboardingLayout";
import api from "../../../shared/api/axios";
import { useAuth } from "../../../shared/contexts/AuthContext";

import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

/* -------------------------------------------------------------------------- */
/*  Stripe init                                                               */
/* -------------------------------------------------------------------------- */

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!
);


/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

type BillingAddress = {
  street: string;
  postalCode: string;
  city: string;
  country: string;
};

/* -------------------------------------------------------------------------- */
/*  Inner form (needs Stripe hooks)                                            */
/* -------------------------------------------------------------------------- */

const BillingForm: React.FC = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  /* ---------------- Billing ---------------- */
  const [billing, setBilling] = useState<BillingAddress>({
    street: "",
    postalCode: "",
    city: "",
    country: "",
  });

  /* ---------------- Stripe ---------------- */
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  /* ---------------- UI ---------------- */
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* -------------------------------------------------------------------------- */
  /*  Load SetupIntent                                                         */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.post("/billing/stripe/setup-intent");
        setClientSecret(res.data.clientSecret);
      } catch {
        setError("Failed to initialize payment system.");
      }
    })();
  }, []);

  /* -------------------------------------------------------------------------- */
  /*  Validation                                                               */
  /* -------------------------------------------------------------------------- */
  const canActivate =
    billing.street &&
    billing.postalCode &&
    billing.city &&
    billing.country &&
    acceptTerms &&
    stripe &&
    elements &&
    clientSecret;

  /* -------------------------------------------------------------------------- */
  /*  Submit                                                                    */
  /* -------------------------------------------------------------------------- */
  const handleActivate = async () => {
    if (!canActivate || loading) return;

    setLoading(true);
    setError(null);

    try {
      /* 1️⃣ Confirm card setup */
      const result = await stripe!.confirmCardSetup(clientSecret!, {
        payment_method: {
          card: elements!.getElement(CardElement)!,
          billing_details: {
            address: {
              line1: billing.street,
              postal_code: billing.postalCode,
              city: billing.city,
              country: billing.country,
            },
          },
        },
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      /* 2️⃣ Attach payment method */
      await api.post("/billing/stripe/attach", {
        paymentMethodId: result.setupIntent.payment_method,
        billingAddress: billing,
      });

      /* 3️⃣ Finalize advertiser account */
      const res = await api.post("/advertiser/register-complete", {
        billingAddress: billing,
        acceptedTerms: {
          version: "2024-09",
          acceptedAt: new Date().toISOString(),
          country: billing.country,
        },
      });

      if (user) {
        setUser({
          ...user,
          advertiserProfile: res.data.advertiserProfile,
        });
      }

      navigate("/advertisementstart", { replace: true });
    } catch (e: any) {
      setError(
        e?.message ||
          e?.response?.data?.msg ||
          "Failed to activate advertiser account."
      );
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*  Render                                                                   */
  /* -------------------------------------------------------------------------- */
  return (
    <AdvertiserOnboardingLayout>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 26, marginBottom: 6 }}>
          Billing & payment
        </h1>
        <p style={{ fontSize: 14, color: "#9ca3af" }}>
          Add a payment method to start running advertisements.
        </p>
      </header>

      {error && (
        <div
          style={{
            background: "rgba(127,29,29,0.3)",
            padding: 12,
            borderRadius: 10,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      <section
        style={{
          background: "#0D101B",
          border: "1px solid #22283a",
          borderRadius: 16,
          padding: 20,
        }}
      >
        {/* Billing address */}
        <h3 style={{ marginBottom: 12 }}>Billing address</h3>

        <Field label="Street address">
          <input
            value={billing.street}
            onChange={(e) =>
              setBilling({ ...billing, street: e.target.value })
            }
            style={input}
          />
        </Field>

        <div style={{ display: "flex", gap: 10 }}>
          <Field label="Postal code">
            <input
              value={billing.postalCode}
              onChange={(e) =>
                setBilling({ ...billing, postalCode: e.target.value })
              }
              style={input}
            />
          </Field>

          <Field label="City">
            <input
              value={billing.city}
              onChange={(e) =>
                setBilling({ ...billing, city: e.target.value })
              }
              style={input}
            />
          </Field>
        </div>

        <Field label="Country">
          <input
            value={billing.country}
            onChange={(e) =>
              setBilling({ ...billing, country: e.target.value })
            }
            style={input}
          />
        </Field>

        {/* Payment */}
        <div style={{ marginTop: 22 }}>
          <h3 style={{ marginBottom: 10 }}>Payment method</h3>

          <div
            style={{
              padding: "12px",
              borderRadius: 12,
              border: "1px solid #374151",
              background: "#020617",
            }}
          >
            <CardElement
              options={{
                hidePostalCode: true,
                style: {
                  base: {
                    color: "#e5e7eb",
                    fontSize: "14px",
                    "::placeholder": { color: "#6b7280" },
                  },
                },
              }}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          marginTop: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <label style={{ fontSize: 13, display: "flex", gap: 8 }}>
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
          />
          I accept the Advertising Terms & Privacy Policy
        </label>

        <button
          onClick={handleActivate}
          disabled={!canActivate || loading}
          style={{
            padding: "10px 20px",
            borderRadius: 999,
            background: "#22c55e",
            color: "#020617",
            fontWeight: 600,
            border: "none",
            opacity: !canActivate || loading ? 0.6 : 1,
            cursor: "pointer",
          }}
        >
          {loading ? "Activating…" : "Activate account"}
        </button>
      </footer>
    </AdvertiserOnboardingLayout>
  );
};

/* -------------------------------------------------------------------------- */
/*  Wrapper with Stripe Elements                                              */
/* -------------------------------------------------------------------------- */

const Billing: React.FC = () => (
  <Elements stripe={stripePromise}>
    <BillingForm />
  </Elements>
);

export default Billing;

/* -------------------------------------------------------------------------- */
/*  Reusable Field                                                            */
/* -------------------------------------------------------------------------- */

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <label style={{ fontSize: 13, color: "#9ca3af" }}>{label}</label>
    {children}
  </div>
);

const input = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #374151",
  background: "#020617",
  color: "#e5e7eb",
};
